import { Button, Icon, Tooltip } from "@mindlercare/ui-web";

import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
import { useMindlerApi } from "api/apiv2";
import { usePostTreatmentStatus } from "api/usePatientQueries";
import { useAlertContext } from "components/booking/AlertContext";
import { TreatmentStatusDropdDown } from "components/treatmentStatusDropdown";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import { Slot } from "core/types";
import { addHours, fromUnixTime, isAfter, isBefore } from "date-fns";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Option } from "../../patients/patientCard/patientData/patientTreatmentStatusModal/patientTreatmentStatusModal.types";

const { useQueryClient, useQuery } = ReactQueryV5;

export const TreatmentStatusSection = ({ slot }: { slot: Slot }) => {
  const { t } = useTranslation();
  const { showAlert } = useAlertContext();
  const [currentSelectedOption, setCurrentSelectedOption] =
    useState<Option | null>(null);
  const [saveTreatmentStatusIsDisabled, setSaveTreatmentStatusIsDisabled] =
    useState(true);
  const queryClient = useQueryClient();
  const apiv2 = useMindlerApi();
  const { mutateAsync: postTreatmentStatus } = usePostTreatmentStatus();

  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(slot.userId),
      },
      {
        enabled: !!slot.userId,
      },
    ),
  );

  const eventType =
    currentSelectedOption?.type === CONSTANTS.ACTIVE_TREATMENT.IN_TREATMENT
      ? CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_START
      : CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END;

  const alreadyInTreatmentStatus =
    !patient?.treatmentStatus?.lastTreatmentEvent?.endTreatmentReason &&
    currentSelectedOption?.type === CONSTANTS.ACTIVE_TREATMENT.IN_TREATMENT;
  const alreadyEndedTreatmentStatus =
    patient?.treatmentStatus?.lastTreatmentEvent?.endTreatmentReason ===
    currentSelectedOption?.type;

  const highlightReviewAfterMeeting =
    isBefore(fromUnixTime(slot.startsAt), new Date()) &&
    isAfter(addHours(fromUnixTime(slot.startsAt), 1), new Date());

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSaveTreatmentStatusIsDisabled(
      alreadyEndedTreatmentStatus || alreadyInTreatmentStatus,
    );
  }, [alreadyEndedTreatmentStatus, alreadyInTreatmentStatus]);

  const handleSave = async () => {
    if (!patient) return;
    const data = {
      patientUserId: patient.userId,
      type: eventType,
      ...(eventType === CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END && {
        endTreatmentReason: currentSelectedOption?.type,
      }),
    };
    try {
      await postTreatmentStatus(data);
      queryClient.invalidateQueries(
        apiv2.patients.getByUserId.queryFilter({
          patientUserId: patient.userId,
        }),
      );

      setSaveTreatmentStatusIsDisabled(true);
      showAlert(t("TREATMENT_STATUS_UPDATED"), "checkmark");
    } catch (error) {
      showAlert(t("TREATMENT_STATUS_SAVE_FAILED"), "warning");
      console.error("Failed to save treatment status update:", error);
    }
  };

  return (
    <TreatmentStatusContainer>
      <TreatmentStatusHeading>
        <Typography insensitive variant="h3" sx={{ marginRight: "10px" }}>
          {t("IS_PATIENT_IN_TREATMENT")}
        </Typography>
        <Tooltip
          content={
            <Trans
              i18nKey="TREATMENT_STATUS_INFO"
              components={{
                strong: <strong />,
                em: <em />,
                li: <li />,
                ul: <ul />,
                h4: <h4 />,
              }}
            />
          }
        >
          <Icon.TreatmentInfo />
        </Tooltip>
      </TreatmentStatusHeading>
      {highlightReviewAfterMeeting && (
        <TreatmentStatusHighlightContainer>
          <TreatmentStatusHighlightDot />
          <Typography insensitive variant="body-small" color="GRAY_DARK">
            {t("REVIEW_AFTER_MEETING")}
          </Typography>
        </TreatmentStatusHighlightContainer>
      )}
      <TreatmentStatusDropdownDiv>
        <TreatmentStatusDropdDown
          patientUserId={patient?.userId}
          currentSelectedOption={currentSelectedOption}
          onChange={setCurrentSelectedOption}
        />
        <Button
          variant="primary"
          disabled={saveTreatmentStatusIsDisabled}
          onClick={handleSave}
        >
          {t("Save")}
        </Button>
      </TreatmentStatusDropdownDiv>
    </TreatmentStatusContainer>
  );
};

const TreatmentStatusContainer = styled.div`
  padding: 32px 40px 0;
`;

const TreatmentStatusHeading = styled.div`
  display: flex;
  align-items: center;
`;

const TreatmentStatusDropdownDiv = styled.div`
  display: flex;
  align-items: center;
  padding-top: 10px;
  gap: ${(props) => props.theme.SPACING.MD};
`;

const TreatmentStatusHighlightContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

const TreatmentStatusHighlightDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
  margin-right: 14px;
  background-color: ${({ theme }) => theme.COLORS.SECONDARY};

  @keyframes pulse-secondary {
    0% {
      box-shadow: 0 0 0 0px rgba(250, 145, 70, 0.4);
    }
    100% {
      box-shadow: 0 0 0 10px rgba(250, 145, 70, 0);
    }
  }
  animation: pulse-secondary 2s infinite;
`;
